import { Injectable } from "@angular/core";
import { FilterItem, TableCell, TableColumn, TableRow } from "../../models/common-table";

export interface SelectListItem {
  display_name: string;
  id: string | number;
}

export type MultiSelectItem = string | SelectListItem;

@Injectable({
  providedIn: "root",
})
export class CommonTableFilterService {
  public filterRows(rows: TableRow[], columns: TableColumn[], filterItems: FilterItem[]): TableRow[] {
    if ((filterItems?.length ?? 0) === 0) {
      return [...rows];
    }

    return rows.filter((row: TableRow) => {
      return !filterItems.some((filterItem: FilterItem) => {
        const matchingColumnIndex: number = columns.findIndex((column: TableColumn) => {
          return (column.label || column.title) === (filterItem.columnLabel || filterItem.columnTitle);
        });

        const cellsToSearch: TableCell<unknown>[] = this.getCellsToSearch(row, matchingColumnIndex, filterItem);

        return !cellsToSearch.some((cell: TableCell<unknown>) =>
          this.matchesAnySearchText(cell, filterItem.searchText),
        );
      });
    });
  }

  public filterMultiSelectItems(
    items: MultiSelectItem[],
    searchValue: string,
    selectedValues: string[],
  ): MultiSelectItem[] {
    if ((items?.length ?? 0) === 0) {
      return [];
    }

    const normalizedSearch: string = (searchValue || "").toLowerCase();

    const filteredItems: MultiSelectItem[] = items.filter((item: MultiSelectItem) => {
      if (!normalizedSearch) {
        return true;
      }

      if (typeof item === "string") {
        return item.toLowerCase().includes(normalizedSearch);
      }

      const displayValue: string = String(item.display_name || item.id || "");
      return displayValue.toLowerCase().includes(normalizedSearch);
    });

    const selectedItems: MultiSelectItem[] = items.filter((item: MultiSelectItem) => {
      if (typeof item === "string") {
        return selectedValues.includes(item);
      }

      const itemId: string = String(item.id || "");
      const itemDisplayName: string = String(item.display_name || "");
      return selectedValues.includes(itemId) || selectedValues.includes(itemDisplayName);
    });

    return [...new Set<MultiSelectItem>([...filteredItems, ...selectedItems])];
  }

  private getCellsToSearch(row: TableRow, matchingColumnIndex: number, filterItem: FilterItem): TableCell<unknown>[] {
    const columnName: string = (filterItem.columnLabel || filterItem.columnTitle || "").toUpperCase();

    if (columnName === "ALL") {
      return row;
    }

    if (matchingColumnIndex >= 0) {
      const matchingCell: TableCell<unknown> | undefined = row[matchingColumnIndex];
      return matchingCell ? [matchingCell] : [];
    }

    return [];
  }

  private matchesAnySearchText(cell: TableCell<unknown>, searchTextList: string[]): boolean {
    const normalizedSearchTextList: string[] = (searchTextList || []).map((searchText: string) =>
      searchText.toLowerCase(),
    );

    return normalizedSearchTextList.some((searchText: string) => this.matchesSingleSearchText(cell, searchText));
  }

  private matchesSingleSearchText(cell: TableCell<unknown>, rawSearchText: string): boolean {
    const cellValue: unknown = cell.selected || cell.value;
    if (cellValue === undefined || cellValue === null) {
      return false;
    }

    const normalizedCellValue: string = cellValue.toString().toLowerCase();
    if (!normalizedCellValue) {
      return false;
    }

    if (rawSearchText.startsWith("=")) {
      const exactMatchText: string = rawSearchText.slice(1);
      return normalizedCellValue === exactMatchText;
    }

    return normalizedCellValue.includes(rawSearchText);
  }
}
